import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  IonContent,
  IonIcon,
  IonLabel,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonToolbar,
  useIonViewDidEnter,
} from "@ionic/react";
import {
  Category,
  Order,
  Price,
  TableId,
  TableServiceOrder,
  TableServiceOrderStatus,
  TableServiceSettings,
} from "@greeter/core";

import { classNames, doNothing, Query } from "@greeter/util";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  fromOrder,
  GradientButton,
  Receipt,
  useHiddenTabBar,
} from "@greeter/matter";
import {
  arrowForward,
  banOutline,
  chevronDownCircleOutline,
  wineOutline,
} from "ionicons/icons";
import { Duration, Interval, intervalToDuration } from "date-fns";
import { useInterval } from "@greeter/hooks";
import { logger } from "@greeter/log";

import css from "./TableServiceOverviewPage.module.scss";

const NAME = "[TableServiceOverviewPage]";
const log = logger(NAME);

/**
 * Tryout for better readability and easy use of checking status
 * it really removes the annoying dot notation most of the time
 */
function is(
  tableServiceOrder: TableServiceOrder | TableServiceOrderStatus,
  ...status: TableServiceOrderStatus[]
) {
  const orderStatus =
    typeof tableServiceOrder === "object"
      ? tableServiceOrder.status
      : tableServiceOrder;

  // We use a simple for loop to keep it close to the metal.
  for (let i = 0; i < status.length; i++) {
    if (status[i] === orderStatus) {
      return true; // Return early
    }
  }

  return false;
}

function useLatestData<T>(q: Query<T>) {
  const [data, setData] = useState<T | undefined>();

  useEffect(() => {
    switch (q.type) {
      case "done":
        setData(q.data);
        break;
    }
  }, [q]);

  return data;
}

const ButtonText = ({ children }) => {
  return (
    <span style={{ fontWeight: "inherit", fontSize: "inherit" }}>
      {children}
    </span>
  );
};

export const AcceptButton = ({ onClick }) => {
  return (
    <GradientButton {...classNames(css.Flex, css.Button)} onClick={onClick}>
      <ButtonText>Godkend</ButtonText>
      <IonIcon icon={arrowForward} />
    </GradientButton>
  );
};

export const RejectButton = ({ onClick }) => {
  return (
    <Button
      {...classNames(css.Flex, css.Button, css.RejectButton)}
      onClick={onClick}
    >
      <IonIcon icon={banOutline} />
      <ButtonText>Afvis</ButtonText>
    </Button>
  );
};

export const ServeButton = ({ onClick }) => {
  return (
    <GradientButton
      {...classNames(css.Flex, css.Button, css.End)}
      onClick={onClick}
    >
      <ButtonText>Server</ButtonText>
      <IonIcon icon={wineOutline} />
    </GradientButton>
  );
};

type TableServiceOrderHeaderProps = React.PropsWithChildren & {
  status: TableServiceOrderStatus;
  orderedAt: Date;
  tableId: TableId;
  total: number;
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
  blink?: boolean;
};
export const TableServiceOrderHeader: React.FC<
  TableServiceOrderHeaderProps
> = ({
  orderedAt,
  tableId,
  total,
  onClick = doNothing,
  status,
  style,
  className,
  blink = false,
}) => {
  const [orderedSince, setOrderedSince] = useState<Duration>();

  useInterval(
    () => {
      const interval: Interval = { start: orderedAt, end: new Date() };
      const duration: Duration = intervalToDuration(interval);
      setOrderedSince(duration);
    },
    1000,
    [orderedAt]
  );

  return (
    <div
      {...classNames(
        css.TableServiceOrderHeader,
        is(status, "pending")
          ? css.Gradient
          : is(status, "accepted")
          ? css.Yellow
          : css.Default,
        blink && css.Blink
      )}
      onClick={onClick}
    >
      <div className={css.TableServiceOrderHeaderContent}>
        <div className={css.TableServiceOrderHeaderColumn}>
          {is(status, "completed") ? (
            <div>Serveret for</div>
          ) : (
            <div>Bestilt for</div>
          )}
          <div>{orderedSince?.minutes ?? 0} min. siden</div>
        </div>

        <div className={css.TableServiceOrderHeaderColumn}>
          <div>{tableId.area}</div>
          <div>Bord {tableId.tableNumber}</div>
        </div>

        <div className={css.TableServiceOrderHeaderColumn}>
          <div>Ordresum</div>
          <div>{total} kr</div>
        </div>
      </div>
    </div>
  );
};

type TableServiceOrderBodyProps = React.PropsWithChildren & {
  order: Order;
};
export const TableServiceOrderBody: React.FC<TableServiceOrderBodyProps> = ({
  order,
}) => {
  return (
    <div className={css.TableServiceOrderBody}>
      {order && order.orderLines && <Receipt lines={fromOrder(order)} />}
    </div>
  );
};

export type TableServiceOrderFooterProps = {
  status: TableServiceOrderStatus;
  onReject: () => void;
  onAccept: () => void;
  onServe: () => void;
};

export const TableServiceOrderFooter: React.FC<
  TableServiceOrderFooterProps
> = ({ status, onReject, onAccept, onServe }) => {
  return (
    <div
      {...classNames(
        css.TableServiceOrderFooter,
        is(status, "accepted") && css.End
      )}
    >
      {is(status, "pending") ? (
        <>
          <RejectButton onClick={onReject} />
          <AcceptButton onClick={onAccept} />
        </>
      ) : (
        <ServeButton onClick={onServe} />
      )}
    </div>
  );
};

type TableServiceOrderCallback = (tableServiceOrder: TableServiceOrder) => void;

type TableServiceOrderCard = {
  tableServiceOrder: TableServiceOrder;
  onAccept?: TableServiceOrderCallback;
  onReject?: TableServiceOrderCallback;
  onServe?: TableServiceOrderCallback;
};

export const TableServiceOrderCard: React.FC<TableServiceOrderCard> = ({
  tableServiceOrder,
  onReject = doNothing,
  onAccept = doNothing,
  onServe = doNothing,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={css.TableServiceOrderCard}>
      <TableServiceOrderHeader
        status={tableServiceOrder.status}
        orderedAt={tableServiceOrder.createdAt ?? new Date()}
        tableId={tableServiceOrder.tableId}
        total={tableServiceOrder.order.total}
        onClick={() => setOpen((o) => !o)}
        blink={!open}
      />
      {open && (
        <>
          <TableServiceOrderBody order={tableServiceOrder.order} />
          {!is(tableServiceOrder.status, "completed") && (
            <TableServiceOrderFooter
              status={tableServiceOrder.status}
              onServe={() => onServe(tableServiceOrder)}
              onAccept={() => onAccept(tableServiceOrder)}
              onReject={() => onReject(tableServiceOrder)}
            />
          )}
        </>
      )}
    </div>
  );
};

// This really should be places in date or something
export const yearsToMonths = (years: number) => years * 12;
export const monthsToWeeks = (months: number) => months * 4.348214166666667;
export const weeksToDays = (weeks: number) => weeks * 7;
export const daysToHours = (days: number) => days * 24;
export const hoursToMinutes = (hours: number) => hours * 60;
export const daysToMinutes = (days: number) =>
  hoursToMinutes(daysToHours(days));
export const weeksToMinutes = (weeks: number) =>
  daysToMinutes(weeksToDays(weeks));
export const monthsToMinutes = (months: number) =>
  weeksToMinutes(monthsToWeeks(months));
export const yearsToMinutes = (years: number) =>
  monthsToMinutes(yearsToMonths(years));

export const totalDurationInMinutes = ({
  years,
  months,
  weeks,
  days,
  hours,
  minutes,
}: Duration) => {
  return (
    yearsToMinutes(years ?? 0) +
    monthsToMinutes(months ?? 0) +
    weeksToMinutes(weeks ?? 0) +
    daysToMinutes(days ?? 0) +
    hoursToMinutes(hours ?? 0) +
    (minutes ?? 0)
  );
};

export type TableServiceOverviewPageProps = React.PropsWithChildren & {
  settings: Query<TableServiceSettings>;
  tableServiceOrders: Query<TableServiceOrder[]>;
  onAccept?: TableServiceOrderCallback;
  onReject?: TableServiceOrderCallback;
  onServe?: TableServiceOrderCallback;
  onRefresh?: () => void;
  onChangeMode?: (mode: TableServiceSettings["mode"]) => void;
};

export const TableServiceOverviewPage: React.FC<
  TableServiceOverviewPageProps
> = ({
  tableServiceOrders,
  settings,
  onAccept = doNothing,
  onReject = doNothing,
  onServe = doNothing,
  onChangeMode = doNothing,
  onRefresh = doNothing,
}) => {
  const leftSideOrders =
    tableServiceOrders.type === "done"
      ? tableServiceOrders.data.filter((tso) => is(tso, "pending"))
      : [];
  const [rightColumnOrders, setRightColumnOrders] = useState<
    TableServiceOrder[]
  >([]);

  const updateRightColumn = useCallback(() => {
    setRightColumnOrders(() => {
      if (tableServiceOrders.type !== "done") return [];

      const tsos = tableServiceOrders.data.filter((tso) => {
        // const duration: Interval = {
        //   start: tso.completedAt ?? new Date(),
        //   end: new Date(),
        // };
        // const minutesSinceCompleted = totalDurationInMinutes(
        //   intervalToDuration(duration)
        // );
        return is(tso, "accepted") || is(tso, "completed");
      });

      return tsos;
    });
  }, [tableServiceOrders]);

  useEffect(() => {
    updateRightColumn();
  }, [updateRightColumn]);

  useInterval(updateRightColumn, 5000, [tableServiceOrders]);

  const onModeClick = (mode: TableServiceSettings["mode"]) => () =>
    onChangeMode(mode);

  /**
   * Counteracts the flutter that happens when fetching new settings.
   * Holds on to the last value provided by the query.
   */
  const latestFeatured = useLatestData(settings);

  return (
    <IonPage>
      <IonToolbar
        style={{ "--ion-toolbar-background": "var(--gm-color-alt-bg)" }}
      >
        <IonSegment
          value={settings.type === "done" ? settings.data.mode : "open"}
          slot="end"
          className={css.Green}
        >
          <IonSegmentButton value="open" onClick={onModeClick("open")}>
            <IonLabel>Åbent</IonLabel>
          </IonSegmentButton>
          {latestFeatured?.featuredCategory && (
            <IonSegmentButton
              key={latestFeatured.featuredCategory.name}
              value="featured"
              onClick={onModeClick("featured")}
            >
              {latestFeatured.featuredCategory.name}
            </IonSegmentButton>
          )}
          <IonSegmentButton value="closed" onClick={onModeClick("closed")}>
            <IonLabel>Lukket</IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </IonToolbar>
      <IonContent>
        <IonRefresher
          slot="fixed"
          onIonRefresh={async (ev: any) => {
            await onRefresh();
            ev.target.complete();
          }}
        >
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            refreshingSpinner="circles"
            refreshingText="Genopfrisker ordrer"
          />
        </IonRefresher>
        <div className={css.TableServiceOverviewPage}>
          <div {...classNames(css.LeftColumn, css.Column)}>
            {leftSideOrders.length === 0 ? (
              <div className={css.Center}>
                <Card className={css.Card}>
                  <CardContent className={css.CardContent}>
                    <CardTitle>Ingen ordrer endnu</CardTitle>
                    <CardDescription>
                      Når en gæst bestiller, vil ordren blive vist her.
                    </CardDescription>
                    <div>
                      <b>Greeter Tip:</b> Prøv at fortæl dine gæster at de kan
                      bestille fra deres bord
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              leftSideOrders.map((tso) => (
                <TableServiceOrderCard
                  key={tso.id}
                  tableServiceOrder={tso}
                  onAccept={(tso) => {
                    onAccept(tso);
                  }}
                  onReject={onReject}
                />
              ))
            )}
          </div>
          <div {...classNames(css.RightColumn, css.Column)}>
            {leftSideOrders.length > 0 && rightColumnOrders.length === 0 ? (
              <div className={css.Center}>
                <Card className={css.Card}>
                  <CardContent>
                    <CardTitle>Ingen accepterede ordrer at vise</CardTitle>
                    <CardDescription>
                      Når du acceptere en ordre fra venstre side, vil ordren
                      rykke herover.
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>
            ) : (
              rightColumnOrders.map((tso) => (
                <TableServiceOrderCard
                  key={tso.id}
                  tableServiceOrder={tso}
                  onServe={onServe}
                />
              ))
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};
